import { signal, computed, effect, batch } from "@preact/signals-react";
import "@/com_lib/linq";
import { produce, original } from "immer";
import { saveCart } from "@/components/Common";
import { useEffect, useState } from "react";

// In cart, the UI can change product of a bundle.
// So need this info for changing.
export interface ChangeBundleParam{
	bundleId: number,
	variationKey: string,
	productIndex: number, // The position of the productId in variationKey
}

// Key is the variation key (stored in this variable: BundleAndVariation.variationKey), which is product ids seperated by ",", calculated by getBundleCurrProductIds()
// Value is the count.
export type BundleVariation = Map<string, number>;
export interface BundleAndProductIds {
	bundleIds: Set<number>;
	productIds: Set<number>;
}
export interface RoomGroupNameTrans{
	name: string;
	name_cht: string;
	name_chs: string;
}
export interface Cart {
	projectSlug: string;
	bundles: Map<number, BundleVariation>; // key is the bundle id, value is BundleVariation.
	products: Map<number, number>; // key is the product id, value is count.
	roomGroupName: Map<string, BundleAndProductIds>; // Key is always the english room group name. Remember the last added room group name. Can leave the deleted items in here.
	roomGroupNameTrans: Map<string, RoomGroupNameTrans>; // Key is the english room group name.
	roomGroupNameOrder: string[];
}

export function createCart(projectSlug: string) {
	const result: Cart = {
		projectSlug,
		bundles: new Map<number, BundleVariation>(),
		products: new Map<number, number>(),
		roomGroupName: new Map<string, BundleAndProductIds>(),
		roomGroupNameTrans: new Map<string, RoomGroupNameTrans>(),
		roomGroupNameOrder: []
	}

	return result;
}

const defaultCart = createCart("");

export const cartSignal = signal<Cart>(defaultCart); //the default state

export function useCart() {
	const [currCart, set_currCart] = useState<Cart>(null);
	useEffect(() => {
		const dispose = effect(() => {
			//console.log("Cart updated");
			set_currCart(cartSignal.value);
		});

		// Destroy effect and subscriptions
		return dispose;
	}, []);

	return currCart;
}

//derived state based on whether a user exists
export const cartCount = computed(() => {
	const cart = cartSignal.value;
	let total = 0;
	if (cart) {
		for (const [bundleId, bundleVariation] of cart.bundles.entries()) {
			for (const [variationKey, value] of bundleVariation.entries()) {
				//console.log(variationKey, value)
				total += value;
			}
		}
		for (const [productId, value] of cart.products.entries()) {
			total += value;
		}
	}
	return total;
});

export function useCartCount() {
	const [_cartCount, set_cartCount] = useState(cartCount.value);
	useEffect(() => {
		const dispose = effect(() => {
			set_cartCount(cartCount.value);
		});

		// Destroy effect and subscriptions
		return dispose;
	}, []);

	return _cartCount;
}

// This can also handle addBundle(), so remove the function addBundle();
export function updateBundle(bundleId: number, variationKey: string, countChanges: number) {
	if (!variationKey) {
		throw "Missing variationKey";
	}
	const cart = cartSignal.value;

	const newCart = produce(cart, draft => {
		const oldItems = original(draft.bundles);
		let bundleVariation: BundleVariation;
		if (oldItems.has(bundleId)) {
			bundleVariation = new Map(oldItems.get(bundleId));
			if (bundleVariation.has(variationKey)) {
				const oldCount = bundleVariation.get(variationKey);
				const newCount = oldCount + countChanges;
				bundleVariation.set(variationKey, newCount);
			}
			else {
				bundleVariation.set(variationKey, countChanges);
			}
		}
		else {
			bundleVariation = new Map<string, number>();
			bundleVariation.set(variationKey, countChanges);
		}
		draft.bundles.set(bundleId, bundleVariation);
	})
	cartSignal.value = newCart;

	saveCart(newCart);
}

export function updateBundleArrayVersion(allBundleIdAndVariationKeys: { bundleId: number, variationKey: string }[]) {
	batch(() => {
		for (let index = 0; index < allBundleIdAndVariationKeys.length; index++) {
			const element = allBundleIdAndVariationKeys[index];
			updateBundle(element.bundleId, element.variationKey, 1);
		}
	});
}

export function changeBundleVariation(changeBundleParam: ChangeBundleParam, productId: number){
	const cart = cartSignal.value;

	let newKey: string;
	const newCart = produce(cart, draft => {
		const bundles = draft.bundles;
		const bundleVariation: BundleVariation = bundles.get(changeBundleParam.bundleId);
		const oldCount = bundleVariation.get(changeBundleParam.variationKey);
		const oldKeyArray = changeBundleParam.variationKey.split(",");
		oldKeyArray[changeBundleParam.productIndex] = productId.toString();
		newKey = oldKeyArray.join(",");
		if (changeBundleParam.variationKey != newKey){ // If the new key is same as old key, then should not change the cart.
			if (bundleVariation.has(newKey)){
				const newCount = bundleVariation.get(newKey) + oldCount;
				bundleVariation.set(newKey, newCount);
			}
			else{
				bundleVariation.set(newKey, oldCount);
			}
			bundleVariation.delete(changeBundleParam.variationKey);
		}
	})
	cartSignal.value = newCart;

	saveCart(newCart);

	return newKey;
}

export function removeBundle(bundleId: number, variationKey: string) {
	const cart = cartSignal.value;

	const newCart = produce(cart, draft => {
		draft.bundles.get(bundleId).delete(variationKey);
	})
	cartSignal.value = newCart;

	saveCart(newCart);
}

export function addProduct(productId: number, count: number) {
	const cart = cartSignal.value;

	const newCart = produce(cart, draft => {
		const oldItems = original(draft.products);
		if (oldItems.has(productId)) {
			const oldCount = oldItems.get(productId);
			const newCount = oldCount + count;
			draft.products.set(productId, newCount);
		}
		else {
			draft.products.set(productId, count);
		}
	})
	cartSignal.value = newCart;

	saveCart(newCart);
}

export function updateProduct(productId: number, count: number) {
	const cart = cartSignal.value;

	const newCart = produce(cart, draft => {
		draft.products.set(productId, count);
	})
	cartSignal.value = newCart;

	saveCart(newCart);
}

export function removeProduct(productId: number) {
	const cart = cartSignal.value;

	const newCart = produce(cart, draft => {
		draft.products.delete(productId);
	})
	cartSignal.value = newCart;

	saveCart(newCart);
}

export function clearCart() {
	const cart = cartSignal.value;

	const newCart = produce(cart, draft => {
		draft.bundles.clear();
		draft.products.clear();
		draft.roomGroupName.clear();
		draft.roomGroupNameTrans.clear();
		draft.roomGroupNameOrder = [];
	})
	cartSignal.value = newCart;

	saveCart(newCart);
}